import { Fragment } from 'react';
import { useQuery } from '@apollo/client';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { Box, Button, EmptyState, List } from '@pocketlaw/tetris';
import { Falcon } from '@pocketlaw/tetris/macro';
import { useCompanyFeatures } from 'app/domains/companies';
import { SidebarLoader } from 'app/domains/documents/SidebarLoader';
import { useACLCan } from 'app/domains/filesystem';
import { AccessManagementList } from 'app/domains/filesystem/components/AccessManagement/AccessManagementList';
import { AccessManagementOwners } from 'app/domains/filesystem/components/AccessManagement/AccessManagementOwners';
import { AccessManagementSearch } from 'app/domains/filesystem/components/AccessManagement/AccessManagementSearch';
import { useActiveDocument, DocumentDrawerHeader } from 'shared/domains/documents';
import { AutoSave } from 'shared/domains/forms/components/AutoSave';
import { DocumentAccessForm } from './DocumentAccessForm';
import { DocumentAccessSidebarDocument } from './DocumentAccessSidebar.gql';
import { DocumentProjectAccess } from './DocumentProjectAccess';
import { DocumentTeamAccessList } from './DocumentTeamAccessList';
import { DocumentUserAccessList } from './DocumentUserAccessList';
import { DrawerContainer, ScrollContainer } from '../../styled';
import { getTitle } from '../../utils';
import { Container } from './styled';
export function Access(props) {
    var _a, _b;
    const { onClose, onGoBack } = props;
    const { id } = useActiveDocument();
    const title = getTitle('access');
    const { isFeatureAvailable, loading: companyFeaturesLoading } = useCompanyFeatures();
    const projectsEnabled = isFeatureAvailable('projects');
    const { data, loading, error, refetch } = useQuery(DocumentAccessSidebarDocument, {
        fetchPolicy: 'network-only',
        nextFetchPolicy: 'cache-and-network',
        skip: companyFeaturesLoading,
        variables: {
            entryId: id,
            projectsEnabled,
        },
    });
    const { accessLevel } = useACLCan({
        userAcls: (_a = data === null || data === void 0 ? void 0 : data.fileSystemEntry) === null || _a === void 0 ? void 0 : _a.acls,
        teamAcls: (_b = data === null || data === void 0 ? void 0 : data.fileSystemEntry) === null || _b === void 0 ? void 0 : _b.teamAcls,
    });
    const showContent = !error && !loading;
    return (<DrawerContainer>
      <DocumentDrawerHeader type="nested" title={title} onClose={onClose} onGoBack={onGoBack}/>

      {loading && (<SidebarLoader title={t({ message: 'Loading access management', comment: 'Loading access management label' })}/>)}

      {error && (<Box alignItems="center" justifyContent="center" height="100vh" mx="xl">
          <EmptyState withAvatar icon={<Falcon icon="exclamation-triangle"/>} title={t({
                message: 'Error',
                comment: 'Failed to load access management label',
            })} description={t({
                message: 'Something went wrong while trying to load the access management',
                comment: 'Failed to load access management description',
            })} actions={<Fragment>
                <Button type="button" appearance="secondary" size="small" onClick={onGoBack}>
                  <Trans comment="Failed to load access management">Go back</Trans>
                </Button>
                <Button type="button" appearance="primary" size="small" onClick={() => refetch()}>
                  <Trans comment="Failed to load access management">Retry</Trans>
                </Button>
              </Fragment>}/>
        </Box>)}

      {showContent && (<ScrollContainer>
          <Container>
            {accessLevel === 'full' && (data === null || data === void 0 ? void 0 : data.fileSystemEntry) && (<DocumentAccessForm from={data === null || data === void 0 ? void 0 : data.fileSystemEntry}>
                <AutoSave debounce={100}/>
                <Box gap="md" flexDirection="column">
                  <AccessManagementSearch from={data} fromParentEntry={data === null || data === void 0 ? void 0 : data.fileSystemEntry}/>
                  <DocumentProjectAccess projectCount={data === null || data === void 0 ? void 0 : data.countFileSystemProjects}/>
                  <AccessManagementOwners from={data}/>
                  <AccessManagementList entryId={id}/>
                </Box>
              </DocumentAccessForm>)}

            {accessLevel !== 'full' && (data === null || data === void 0 ? void 0 : data.fileSystemEntry) && (<Fragment>
                <DocumentProjectAccess projectCount={data === null || data === void 0 ? void 0 : data.countFileSystemProjects}/>
                <List>
                  <DocumentTeamAccessList from={data === null || data === void 0 ? void 0 : data.fileSystemEntry}/>
                  <DocumentUserAccessList from={data === null || data === void 0 ? void 0 : data.fileSystemEntry}/>
                </List>
              </Fragment>)}
          </Container>
        </ScrollContainer>)}
    </DrawerContainer>);
}
